import React from 'react';
import styled from '@emotion/styled';
import type { Dayjs } from 'dayjs';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import { WeddingDateDisplayFormatEnum } from '../../../../../../util/websiteDateUtil';
import { DisplayTypeProps } from './props';

const LocationDateModuleHorizontal = ({
  className,
  city,
  state,
  websiteDate,
  headerFontStyles,
  bodyFontStyles,
}: DisplayTypeProps): JSX.Element | null => {
  const TwoLineModule = ({
    primaryText,
    secondaryText,
    className: twoLineClassname,
  }: {
    primaryText?: string;
    secondaryText?: string;
    className?: string;
  }) => {
    const BlockContainer = styled.div`
      display: flex;
      flex-direction: column;
      align-items: center;
      ${bodyFontStyles}
    `;
    const H3 = styled.h3`
      ${headerFontStyles}
      margin-bottom: 12px;
    `;

    if (primaryText && secondaryText) {
      return (
        <BlockContainer className={twoLineClassname}>
          <H3>{primaryText}</H3>
          {secondaryText}
        </BlockContainer>
      );
    }
    if (primaryText) return <H3 className={twoLineClassname}>{primaryText}</H3>;
    if (secondaryText) return <div className={twoLineClassname}>{secondaryText}</div>;
    return null;
  };

  const hasWeddingDate = Boolean(websiteDate);
  const hasWeddingLocation = Boolean(city || state);

  const Container = styled.div`
    display: flex;
    align-items: center;
    > div {
      width: ${hasWeddingDate && hasWeddingLocation ? '50%' : '100%'};
      ${MEDIA_QUERY.MOBILE} {
        width: 100%;
      }
    }

    ${MEDIA_QUERY.MOBILE} {
      flex-direction: column;
    }
  `;

  const LocationModule = ({
    className: locationClassname,
  }: {
    className?: string;
  }): JSX.Element => (
    <TwoLineModule className={locationClassname} primaryText={city} secondaryText={state} />
  );
  const StyledLocationModule = styled(LocationModule)`
    margin-right: 100px;

    ${MEDIA_QUERY.MOBILE} {
      margin-right: 0;
      margin-bottom: 40px;
    }
  `;

  const DateModule = () => {
    const renderDate = (d?: Dayjs) => (
      <TwoLineModule
        primaryText={`${d?.format('MMMM')} ${d?.format('D')}`}
        secondaryText={d?.format('YYYY')}
      />
    );
    const renderDateFormat = (): React.ReactNode => {
      if (websiteDate?.displayFormat === WeddingDateDisplayFormatEnum.CUSTOM) {
        return <div>{websiteDate.customDateMessage}</div>;
      }
      if (websiteDate?.displayFormat === WeddingDateDisplayFormatEnum.RANGE) {
        const { weddingDateRangeStart, weddingDateRangeEnd } = websiteDate || {};
        const To = styled.div`
          margin: 20px 0;
        `;
        return (
          <>
            {renderDate(weddingDateRangeStart)}
            <To>to</To>
            {renderDate(weddingDateRangeEnd)}
          </>
        );
      }
      return renderDate(websiteDate?.weddingDate);
    };

    return <div className={className}>{renderDateFormat()}</div>;
  };

  return (
    <Container className={className}>
      <StyledLocationModule />
      <DateModule />
    </Container>
  );
};

export default LocationDateModuleHorizontal;
